import {
	Component, ContentChild,
	EventEmitter,
	Input,
	Output, TemplateRef,
	ViewChild,
} from '@angular/core';

import {
	ActivatedRoute,
	Router,
} from '@angular/router';
import {
	ColumnReorderEvent,
	FilterableSettings,
	GridComponent,
	GridDataResult,
	PageChangeEvent,
	PagerSettings,
	SelectableMode,
	SelectableSettings,
} from '@progress/kendo-angular-grid';
import {
	IPagedResponse,
	WithDestroy,
} from '@aex/ngx-toolbox';
import {
	CompositeFilterDescriptor,
	filterBy,
	orderBy,
	SortDescriptor,
} from '@progress/kendo-data-query';
import { FilterExpression } from '@progress/kendo-angular-filter';

import {
	cancelIcon, downloadIcon,
	filterIcon,
	searchIcon,
	selectAllIcon,
	windowRestoreIcon,
	xIcon,
} from '@progress/kendo-svg-icons';
import { instanceToInstance } from 'class-transformer';
import { ToastrService } from 'ngx-toastr';

import {
	getKendoEditorType,
	StringHelper,
	getGridVisibleColumnOrder,
	IDropDownButtonData,
	IPagedData,
	restoreGridVisibleColumnOrder,
	GridColumnItem,
	GridState,
	IAdvanceGridSearch,
	ISearchData,
	LoadGridStateRequest,
	PAGE_SIZES,
	IDetailSearchData,
	hasShowDetailRowRoutine,
	DEFAULT_COMMAND_COLUMN_WIDTH,
} from '@aex/shared/common-lib';

import { ConfigService } from '@aex/shared/root-services';
import { SearchGridService } from '../services/search-grid.service';

@Component({
	selector: 'app-advance-search-grid',
	templateUrl: './advance-search-grid.component.html',
	styleUrls: ['./advance-search-grid.component.scss'],
})
export class AdvanceSearchGridComponent extends WithDestroy() {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ContentChild('gridCellTemplate') gridCellTemplate: TemplateRef<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ContentChild('gridDetailTemplate') gridDetailTemplate: TemplateRef<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ContentChild('gridCommandColumnTemplate') gridCommandColumnTemplate: TemplateRef<any>;

	public readonly svgCancelIcon = cancelIcon;
	public readonly svgDownloadIcon = downloadIcon;
	public readonly svgFilterIcon = filterIcon;
	public readonly svgSearchIcon = searchIcon;
	public readonly svgXIcon = xIcon;
	public readonly svgWindowRestoreIcon = windowRestoreIcon;
	public readonly svgSelectAllIcon = selectAllIcon;

	public checkboxColumnWidth = 65;
	public minResizableWidth = 150;

	// Settings for grid command. Remember to implement template for gridDetailTemplate
	@Input() public haveGridDetailTemplate: boolean = false;

	// Settings for grid command. Remember to implement template for gridCommandColumnTemplate
	@Input() public haveGridCommandTemplate: boolean = false;
	@Input() public gridCommandHeaderClass: string = 'actionsColumnClass';
	@Input() public gridCommandClass: string = 'actionsColumnClass';
	@Input() public gridCommandWidth: number = DEFAULT_COMMAND_COLUMN_WIDTH;
	@Input() public isGridCommandFirst: boolean = true;

	public dateFormat: string;
	@ViewChild('dataGrid', { static: true }) public dataGrid!: GridComponent;
	public gridData: ISearchData[] = [];
	@Input() hasGridStateView: boolean = true;
	@Input() hasQuickActions: boolean = true;
	@Input() hasAdvancedSearch: boolean = true;
	public totalRecordCount: number = 0;
	public gridDataResult: GridDataResult = {
		data: this.filteredGridData,
		total: this.totalRecordCount,
	};
	@Input() public gridViewId: string;
	@Input() public advanceFilterActive: boolean = false;
	@Output() public readonly fetchDataEvent: EventEmitter<{ pageNumber: number, pageSize: number, skip: number }> = new EventEmitter();
	@Output() public readonly clearAdvanceSearchFilterEvent: EventEmitter<void> = new EventEmitter();

	@Input()
	public set loadedGridState(value: LoadGridStateRequest | null) {
		this.loadGridState(value);
	}

	@Output() public readonly loadGridStateEvent: EventEmitter<LoadGridStateRequest> = new EventEmitter<LoadGridStateRequest>();
	@Output() public readonly saveGridStateEvent: EventEmitter<GridState> = new EventEmitter<GridState>();
	@Output() public readonly setAdvanceSearchFilterItemEvent: EventEmitter<IAdvanceGridSearch> = new EventEmitter<IAdvanceGridSearch>();
	@Output() public readonly nameHyperlinkActionEvent: EventEmitter<{ fieldName: string, dataItem: ISearchData }> =
		new EventEmitter<{ fieldName: string, dataItem: ISearchData }>();
	@Output() public readonly checkBoxSelectionChangeEvent: EventEmitter<string[]> = new EventEmitter<string[]>();
	@Output() public readonly gridPageChangeEvent: EventEmitter<PageChangeEvent> = new EventEmitter<PageChangeEvent>();
	public selectableColumns: string[] = [];

	// The selectable grid columns that has been filtered for selection
	public filteredSelectableGridColumns: string[] = [];

	public selectedColumns: string[] = [];
	private _visibleColumns: GridColumnItem[] = [];
	public get visibleColumns(): GridColumnItem[] {
		return this._visibleColumns;
	}

	public set visibleColumns(value: GridColumnItem[]) {
		this._visibleColumns = value;
	}

	// Pagination
	public pageSize = 20;
	public buttonCount = 5;
	public sizes = PAGE_SIZES;
	public skip = 0;
	public currentPage = 1;
	@Input() public pageSettings: PagerSettings = {
		buttonCount: this.buttonCount,
		info: true,
		type: 'numeric',
		pageSizes: this.sizes,
		previousNext: true,
	};
	@Input() public isToggle: boolean = false;
	// Grid Filtering
	@Input() public sort: SortDescriptor[] = [{ field: 'name', dir: 'asc' }]; 		// Grid Sort Value
	public showXLStyleFilter: FilterableSettings = 'menu';            						// Show the XL style filter
	public searchKeyword: string = '';                                						// Search keyword
	@Input() public selectGridKeyId: string = '';     														// Key Id for the Kendo Grid Select
	public gridFilterFields: FilterExpression[] = [];
	public allSelected: boolean = false;                                  // All rows selected
	@Input() public showCheckboxColumn: boolean = false;                  // Show the checkbox column
	@Input() public gridSelectable: SelectableMode = 'single';           	// 'single' | 'multiple'
	@Input() public quickButtonDisabled: boolean = true;
	@Input() public quickActionOptions: IDropDownButtonData[] = [];
	@Input() public hideQuickTools: boolean = true;
	@Input() public exportOptions: IDropDownButtonData[] = [];
	// Grid State
	public gridState: GridState = new GridState();                   // Grid State
	// To keep track of grid state name and lock state
	public gridViewLockState: boolean = false;
	public gridViewStateName: string = '';
	protected readonly defaultFilter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
	public gridFilterValue: CompositeFilterDescriptor = { ...this.defaultFilter };

	constructor(
		protected readonly toastr: ToastrService,
		protected readonly router: Router,
		protected readonly route: ActivatedRoute,
		protected readonly configService: ConfigService,
		protected readonly searchGridService: SearchGridService,
	) {
		super();

		this.dateFormat = this.configService.operatorDateFormat || 'MM-dd-yyyy HH:mm:ss';
	}

	private _filteredGridData: ISearchData[] = [];

	public get filteredGridData(): ISearchData[] {
		return this._filteredGridData;
	};

	public set filteredGridData(value: ISearchData[]) {
		this._filteredGridData = value;
		const gridDataResult = {
			data: this.filteredGridData,
			total: this.totalRecordCount,
		};
		this.gridDataResult = { ...gridDataResult };
	}

	@Input()
	public set pagedData(value: IPagedData<ISearchData>) {
		this.initialiseGridData(value);
	}

	public get displayQuickActionContainer(): boolean {
		return this.hasGridStateView || this.hasQuickActions;
	}

	private _columns: GridColumnItem[] = [];

	public get columns(): GridColumnItem[] {
		return this._columns;
	}

	@Input()
	public set columns(value: GridColumnItem[]) {
		this._columns = value;
		this.selectableColumns = value.map((column) => column.title)
		.sort((a, b) => a.localeCompare(b));
		this.intializeGridColumns();
		this.initialiseGridFilterFields();
	}

	private _selectedItems: string[] = [];

	public get selectedItems(): string[] {
		return this._selectedItems;
	}

	@Input()
	public set selectedItems(value: string[])  // Selected row items
	{
		this._selectedItems = value;
		const selectedCount = this.selectedItems.length;
		const totalCount = this.filteredGridData.length;
		this.allSelected = selectedCount > 0 && selectedCount === totalCount;

		this.checkBoxSelectionChangeEvent.emit(this._selectedItems);
	}

	public get selectableSettings(): SelectableSettings {
		return {
			checkboxOnly: this.showCheckboxColumn,
			mode: this.gridSelectable,
		};
	}

	public get currentGridState(): GridState {
		const gridState: GridState = new GridState();
		gridState.gridViewStateName = this.gridViewStateName;
		gridState.lockState = this.gridViewLockState;
		gridState.selectableColumns = [...this.selectableColumns];
		gridState.columns = [...this.columns];
		gridState.visualColumnOrder = getGridVisibleColumnOrder(this.dataGrid);
		gridState.sort = [...this.sort];
		gridState.filter = { ...this.gridFilterValue };
		gridState.pageSize = this.pageSize;
		gridState.selectedColumns = [...this.selectedColumns];
		gridState.buttonCount = this.buttonCount;
		gridState.sizes = [...this.sizes];
		gridState.skip = this.skip;
		gridState.currentPage = this.currentPage;
		gridState.pageSettings = { ...this.pageSettings };
		gridState.searchKeyword = this.searchKeyword;

		// Double check these flags!
		gridState.hasAdvancedSearch = this.hasAdvancedSearch;
		gridState.advanceFilterActive = this.advanceFilterActive;

		return gridState;
	}

	public quickActionItemClick(itemSelected: IDropDownButtonData): void {
		itemSelected.action();
	}

	public resetGridColumns(): void {
		const { filteredGridColumns, selectedColumns, visibleColumns } = this.searchGridService.resetGridColumns(
			this.selectableColumns,
			this.columns,
		);
		this.filteredSelectableGridColumns = filteredGridColumns;
		this.selectedColumns = selectedColumns;
		this.visibleColumns = visibleColumns;
	}

	public intializeGridColumns(): void {
		if (this.selectedColumns.length > 0) {
			this.filteredSelectableGridColumns = [...this.selectableColumns];
			this.updateVisibleGridColumns();
		} else
			this.resetGridColumns();
	}

	public selectALLGridColumns(): void {
		const selectableColumns = this.columns.map((column) => column.title);
		this.selectedColumns = [...selectableColumns];
		this.selectableColumns = [...selectableColumns];
		this.updateVisibleGridColumns();
	}

	public onFilteredGridColumnChange(filter: string): void {
		const normalizedFilter = filter.toLowerCase();
		this.filteredSelectableGridColumns = this.selectableColumns.filter(column =>
			column.toLowerCase().includes(normalizedFilter),
		);
	}

	public onGridSortChange(event: SortDescriptor[]) {
		this.sort = event;

		const orderedData = orderBy(this.filteredGridData, this.sort);
		this.filteredGridData = [...orderedData];

		this.gridDataResult = {
			data: this.filteredGridData,
			total: this.totalRecordCount,
		};
	}

	// For client-side filtering
	public filterChange(filter: CompositeFilterDescriptor): void {
		this.gridFilterValue = filter;
		this.filteredGridData = this.filterGridData(this.gridData, filter);
	}

	public onGridPageChange(event: PageChangeEvent): void {
		this.pageSize = event.take;
		this.skip = event.skip;

		this.gridPageChangeEvent.emit(event);
	}

	public onGridColumnSelectionChange(): void {
		this.updateVisibleGridColumns();
	}

	public gridColumnReorderEvent(event: ColumnReorderEvent): void {
		const reorderedColumns = this.searchGridService.gridColumnReorderEvent(event, this.visibleColumns, this.showCheckboxColumn);
		this.visibleColumns = [...reorderedColumns];
		this.columns = [...reorderedColumns];
	}

	public exportGridDataItemClick(itemSelected: IDropDownButtonData): void {
		itemSelected.action(this.visibleColumns);
	}

	public onSearchKeywordChange() {
		const searchText = this.searchKeyword.trim().toLowerCase(); // Trim to avoid whitespace issues
		if (!searchText) {
			this.filteredGridData = [...this.gridData];
			return;
		}
		const filteredSearchKeywordData = this.gridData.filter((item) => {
			return StringHelper.searchTextIsInObject(item, searchText);
		});

		this.filteredGridData = [...filteredSearchKeywordData];
	}

	public clearSearch(): void {
		this.searchKeyword = '';
		this.onSearchKeywordChange();
	}

	// Handle the keydown event in the search textbox
	// If we don't prevent the default action, the Enter key will trigger the Filter Window
	public handleKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();           // Prevent default action
			event.stopPropagation();          // Stop event bubbling
			this.onSearchKeywordChange();
		}
	}

	public saveGridState(gridViewName: string): void {
		const gridState = this.currentGridState;
		gridState.gridViewStateName = gridViewName;
		// Has advanced search - indicates whether the flag
		gridState.hasAdvancedSearch = this.hasAdvancedSearch;
		gridState.advanceFilterActive = this.advanceFilterActive;
		this.saveGridStateEvent.emit(gridState);
	}

	public loadGridStateRequest(loadGridStateRequest: LoadGridStateRequest): void {
		this.loadGridStateEvent.emit(loadGridStateRequest);
	}

	public loadGridState(loadGridStateRequest: LoadGridStateRequest): void {
		if (!loadGridStateRequest)
			return;

		if (loadGridStateRequest.clearData) {
			this.cancelAdvSearchClick();
			return;
		}

		if (!loadGridStateRequest.gridState)
			return;

		const gridState = loadGridStateRequest.gridState;

		// Load the grid state
		this.gridViewStateName = loadGridStateRequest.gridViewStateName;
		this.columns = [...gridState.columns];
		this.selectableColumns = [...gridState.selectableColumns];
		this.selectedColumns = [...gridState.selectedColumns];
		this.sort = [...gridState.sort];
		this.gridFilterValue = { ...gridState.filter };
		this.pageSize = gridState.pageSize;
		this.skip = gridState.skip;
		this.buttonCount = gridState.buttonCount;
		this.sizes = [...gridState.sizes];
		this.currentPage = gridState.currentPage;
		this.pageSettings = { ...gridState.pageSettings };
		this.searchKeyword = gridState.searchKeyword;
		this.gridViewLockState = gridState.lockState;
		this.visibleColumns = restoreGridVisibleColumnOrder(this.columns, gridState.visualColumnOrder);
	}

	public cancelAdvSearchClick(): void {
		this.clearFilterAdvSearchClick();
		this.isToggle = false;
		this.advanceFilterActive = false;
		this.currentPage = 1;
		this.fetchGridData();
	}

	public onAdvSearchExpand(): void {
		this.isToggle = true;
	}

	public onAdvSearchCollapse(): void {
		this.isToggle = false;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public toggleSelectAll(event: any): void {
		this.allSelected = event.target.checked;

		if (this.allSelected)
			// Select all rows: Get the Ids of all rows
			this.selectedItems = this.filteredGridData.map((item) => item[this.selectGridKeyId as keyof ISearchData]);
		else
			this.selectedItems = [];

		this.checkBoxSelectionChange();
	}

	public checkBoxSelectionChange(): void {
		this.checkBoxSelectionChangeEvent.emit(this.selectedItems);
	}

	public nameHyperlinkAction(field: string, dataItem: ISearchData) {
		this.nameHyperlinkActionEvent.emit({ fieldName: field, dataItem: dataItem });
	}

	public getDateFormat(type: 'date' | 'dateTime'): string {
		return type === 'date' ? this.dateFormat.split(' ')[0]: this.dateFormat;
	}

	protected initialiseGridFilterFields(): void {
		const filterableColumns = this.columns.filter((column) => column.filterable);
		this.gridFilterFields = filterableColumns.map((column) => {
			return {
				field: column.field || '',
				title: column.title || 'Title Not Set',
				editor: getKendoEditorType(column.data_type),
			};
		});
	}

	protected initialiseGridData(pagedGridData: IPagedResponse<ISearchData>): void {
		this.gridData = pagedGridData?.items;
		this.totalRecordCount = pagedGridData?.total;
		this.filteredGridData = [...pagedGridData?.items];
		this.onGridSortChange(this.sort);
	}

	protected filterGridData(data: ISearchData[], filter: CompositeFilterDescriptor): ISearchData[] {
		return filterBy(data, filter);
	}

	protected fetchGridData(): void {
		this.fetchDataEvent.emit(
			{
				pageNumber: this.currentPage,
				pageSize: this.pageSize,
				skip: this.skip,
			},
		);
	}

	protected updateVisibleGridColumns(): void {
		const selectedSet = new Set(this.selectedColumns);
		const selectedColumns = instanceToInstance(this.columns);
		selectedColumns.forEach((column) => {
			column.hidden = !selectedSet.has(column.title);
		});

		this.visibleColumns = [...selectedColumns];
	}

	protected clearFilterAdvSearchClick(): void {
		this.clearAdvanceSearchFilterEvent.emit();
	}

	public showDetailRow(dataItem: IDetailSearchData): boolean {
		// this part needs to be tested
		if (hasShowDetailRowRoutine(dataItem))
			return dataItem.mayShowDetailRow();

		return true;
	}
}
